import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { Client } from 'src/app/models/client.model';
import { normalizeStediNpi } from 'src/app/models/client.util';
import { EmrDemographics, EmrMetadata, EmrProviders } from 'src/app/models/emr/emr-data.model';
import { ClientServicesService } from 'src/app/services/admin/client-services.service';
import { FirestoreService } from 'src/app/services/firestore.service';
import { SnackService } from 'src/app/services/snack.service';

@Component({
  selector: 'app-client-demo-dialog',
  templateUrl: './client-demo-dialog.component.html',
  styleUrls: ['./client-demo-dialog.component.scss'],
})
export class ClientDemoDialogComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  loading = false;
  serverMessage: string;
  emrList$: Observable<any>;
  emrList: any[];
  title: string;
  isAthenaSelected = false;
  // states = ['AZ', 'CA', 'NV'];

  private readonly athenaRequiredControls = ['welby_practice_id', 'department_id', 'supervising_provider_id'];
  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<ClientDemoDialogComponent>,
    private clientService: ClientServicesService,
    private fs: FirestoreService,
    private snackService: SnackService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.getEMRList();
    this.title = this.data.client?.client_name || this.data.client?.client_id ? 'Edit Client' : 'Create New Client';
    this.form = this.fb.group({
      client_id: [this.data.client?.client_id ?? ''],
      admin_email: [this.data.client?.admin_email ?? '', [Validators.required]],
      client_name: [this.data.client?.client_name ?? '', [Validators.required]],
      client_npi: [this.data.client?.client_npi ?? '', [Validators.required]],
      client_stedi_npi: [this.data.client?.client_stedi_npi ?? '', [Validators.required, Validators.pattern(/^\d{1,10}$/)]],
      rpm_clinical_rate: [this.data.client?.rpm_clinical?.rate ?? 30, [Validators.required]],
      ccm_clinical_rate: [this.data.client?.ccm_clinical?.rate ?? 30, [Validators.required]],
      software_license_rate: [this.data.client?.software_license?.rate ?? 40, [Validators.required]],
      emr: [this.data.client?.emr_data ?? '', [Validators.required]],
      billing_approved: [this.data.client?.billing_approved ?? '', false],
      specialty: [this.data.client?.specialty ?? '', [Validators.required]],
      welby_practice_id: [this.data.client?.emr_data?.welby_practice_id ?? ''],
      department_id: [this.data.client?.emr_data?.metadata?.department_id ?? ''],
      supervising_provider_id: [this.data.client?.emr_data?.metadata?.supervising_provider_id ?? ''],
    });

    this.updateAthenaState(this.form.get('emr').value);

    this.form
      .get('emr')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((emr) => this.updateAthenaState(emr));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getEMRList(): void {
    this.emrList$ = this.fs
      .colWithIds$('ehr_vendors', (ref) => ref.orderBy('display_name'))
      .pipe(
        tap((data) => {
          const selectedEmr = data.find(
            (emr) =>
              emr.vendor === this.data.client.emr_data?.emr_name || emr.vendor === this.data.client.emr_data?.vendor
          );
          this.form.get('emr').setValue(selectedEmr);
          this.updateAthenaState(selectedEmr);
        })
      );
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  async onSubmit() {
    const clientForm = this.form.getRawValue();
    const { doc, ...emrData } = clientForm.emr;
    const emrDataBase = { ...this.data.client?.emr_data, ...emrData };
    const finalEmrData = this.isAthenaSelected ? this.applyAthenaFields(emrDataBase, clientForm) : emrDataBase;
    let client;
    if (this.data.client?.client_name || this.data.client?.client_id) {
      client = {
        admin_email: clientForm.admin_email,
        client_name: clientForm.client_name,
        client_npi: clientForm.client_npi,
        client_stedi_npi: normalizeStediNpi(clientForm.client_stedi_npi),
        emr_data: finalEmrData,
        services: {
          ccm_clinical: { ...this.data.client.ccm_clinical, rate: Number(clientForm.ccm_clinical_rate) },
          rpm_clinical: { ...this.data.client.rpm_clinical, rate: Number(clientForm.rpm_clinical_rate) },
          software_license: { ...this.data.client.software_license, rate: Number(clientForm.software_license_rate) },
        },
        billing_approved: clientForm.billing_approved,
        specialty: clientForm.specialty,
      } as Client;
      await this.clientService.updateClientService(this.data.client.client_id, client).subscribe(
        (data) => {
          this.snackService.genericSnackBar('Client successfully updated', ['success-snackbar'], 3000);
        },
        (error) => {
          console.error(error);
          this.snackService.genericSnackBar('An error occurred updating client', ['error-snackbar'], 3000);
        }
      );
    } else {
      const athenaPayload = this.isAthenaSelected
        ? {
            welby_practice_id: clientForm.welby_practice_id || null,
            department_id: clientForm.department_id || null,
            supervising_provider_id: clientForm.supervising_provider_id || null,
          }
        : {};
      client = this.clientService.buildClient({ ...clientForm, ...athenaPayload });
      await this.clientService.saveNewClient(client);
    }
    const clientFull = { ...this.data.client, client };
    this.dialogRef.close(clientFull);
  }

  private applyAthenaFields(
    emrDataBase: EmrDemographics & { metadata?: EmrMetadata },
    clientForm: { welby_practice_id?: string; department_id?: string; supervising_provider_id?: string }
  ): EmrDemographics & { metadata: EmrMetadata; welby_practice_id: string | null } {
    const practiceId = clientForm.welby_practice_id || null;
    return {
      ...emrDataBase,
      welby_practice_id: practiceId,
      metadata: {
        ...emrDataBase.metadata,
        practice_id: practiceId,
        department_id: clientForm.department_id || null,
        supervising_provider_id: clientForm.supervising_provider_id || null,
      },
    };
  }

  private isAthenaHealth(emr: EmrDemographics | null | undefined): boolean {
    const name = (emr?.display_name ?? emr?.vendor ?? '').toLowerCase();
    return name.includes(EmrProviders.ATHENA);
  }

  private updateAthenaState(emr: EmrDemographics | null | undefined): void {
    this.isAthenaSelected = this.isAthenaHealth(emr);
    this.athenaRequiredControls.forEach((name) => {
      const control = this.form.get(name);
      control?.setValidators(this.isAthenaSelected ? [Validators.required] : []);
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }
}
